import React, { useCallback, useEffect, useState } from 'react';
import { Grid, makeStyles, TextField } from '@material-ui/core';
import moment from 'moment';
import { doSwaggerCall } from '../../../../hooks/useSwaggerClient';
import { useNotify } from '../../../../hooks/useNotify';
import BarChart from './BarChart';
import { formatMoney } from '../../../../utils';
import { DatePicker } from '@material-ui/pickers';
import { useHistory } from 'react-router';

const useStyles = makeStyles({
    title: {
        marginTop: '20px'
    },
    gridItem: {
        marginTop: '10px'
    }
});

const StatisticsTable = ({ agentId }) => {
    const { notifyError } = useNotify();
    const history = useHistory();
    const styles = useStyles();
    const [filters, setFilters] = useState({
        fromDate: moment()
            .subtract(6, 'days')
            .format('YYYY-MM-DD'),
        toDate: moment().format('YYYY-MM-DD')
    });
    const [bookingStats, setBookingStats] = useState([]);
    const [totalGuests, setTotalGuests] = useState([]);
    const [totalVoucher, setTotalVoucher] = useState([]);
    const [totalCommission, setTotalCommission] = useState([]);
    const [refundStats, setRefundStats] = useState([]);

    const loadStatistics = useCallback(async () => {
        // booking stats
        await doSwaggerCall('users/statistics', 'getAgentBookingStats', {
            agentId,
            fromDate: filters.fromDate,
            toDate: filters.toDate
        })
            .then(res => {
                setBookingStats(
                    res.map(({ date, numberOfBookings, grossBookings }) => ({
                        date,
                        'Number of Bookings': numberOfBookings,
                        'Gross Bookings': grossBookings.toFixed(2)
                    }))
                );
            })
            .catch(() => {
                notifyError('Cannot load booking stats for chart');
            });
        // total guests
        await doSwaggerCall('users/statistics', 'getAgentTotalGuestsStats', {
            agentId,
            fromDate: filters.fromDate,
            toDate: filters.toDate
        })
            .then(res => {
                setTotalGuests(res.map(({ date, count }) => ({ date, 'Number of Guests': count })));
            })
            .catch(() => {
                notifyError('Cannot load total guests stats for chart');
            });
        // total voucher
        await doSwaggerCall('users/statistics', 'getAgentTotalVoucherStats', {
            agentId,
            fromDate: filters.fromDate,
            toDate: filters.toDate
        })
            .then(res => {
                setTotalVoucher(
                    res.map(({ date, totalVoucher }) => ({
                        date,
                        'Total Voucher': totalVoucher
                    }))
                );
            })
            .catch(() => {
                notifyError('Cannot load total voucher stats for chart');
            });
        // total commission
        await doSwaggerCall('users/statistics', 'getAgentTotalCommissionStats', {
            agentId,
            fromDate: filters.fromDate,
            toDate: filters.toDate
        })
            .then(res => {
                setTotalCommission(
                    res.map(({ date, totalCommission }) => ({
                        date,
                        'Total Commission': totalCommission.toFixed(2)
                    }))
                );
            })
            .catch(() => {
                notifyError('Cannot load total commission stats for chart');
            });
        // refunds
        await doSwaggerCall('users/statistics', 'getAgentRefundStats', {
            agentId,
            fromDate: filters.fromDate,
            toDate: filters.toDate
        })
            .then(res => {
                setRefundStats(
                    res.map(({ date, numberOfRefunds, totalRefund }) => ({
                        date,
                        'Number of Refunds': numberOfRefunds,
                        'Total Refund': totalRefund.toFixed(2)
                    }))
                );
            })
            .catch(() => {
                notifyError('Cannot load refund stats for chart');
            });
    }, [agentId, filters, notifyError]);

    useEffect(() => {
        if (typeof agentId === 'undefined') {
            history.push('/');
        } else {
            loadStatistics();
        }
    }, [agentId, history, loadStatistics]);

    return (
        <>
            <Grid item xs={12} container direction="column" alignContent="flex-start">
                <Grid item>
                    <h3 style={{ marginBottom: '10px' }}>Statistics</h3>
                </Grid>
                <Grid item className="statistics-datepicker">
                    <Grid item xs={12}>
                        <h5>
                            Only one week is the allowed date range, please select the last day.
                        </h5>
                    </Grid>
                    <DatePicker
                        value={filters.toDate}
                        onChange={newDate => {
                            setFilters({
                                fromDate: moment(newDate)
                                    .subtract(6, 'days')
                                    .format('YYYY-MM-DD'),
                                toDate: moment(newDate).format('YYYY-MM-DD')
                            });
                        }}
                        disableFuture
                        showDaysOutsideCurrentMonth={true}
                        renderInput={props => <TextField {...props} helperText="" />}
                    />
                </Grid>
            </Grid>
            <Grid item xs={12} container alignContent="flex-start" spacing={2}>
                <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <h4>
                        {filters.fromDate} - {filters.toDate}
                    </h4>
                </Grid>
                <Grid item xs={12} lg={6}>
                    <h3 className={styles.title}>Number of Bookings</h3>
                    {bookingStats.length === 0 ? (
                        <Grid item className={styles.gridItem}>
                            <h5>No data available for this date range</h5>
                        </Grid>
                    ) : (
                        <BarChart
                            data={bookingStats}
                            keys={['Number of Bookings']}
                            colors={{ scheme: 'paired' }}
                            axisLeftLegend={'Number of Bookings'}
                            legendOffset={-45}
                        />
                    )}
                </Grid>
                <Grid item xs={12} lg={6}>
                    <h3 className={styles.title}>Gross Bookings</h3>
                    {bookingStats.length === 0 ? (
                        <Grid item className={styles.gridItem}>
                            <h5>No data available for this date range</h5>
                        </Grid>
                    ) : (
                        <BarChart
                            data={bookingStats}
                            keys={['Gross Bookings']}
                            colors={{ scheme: 'category10' }}
                            axisLeftLegend={'Gross Bookings'}
                            format={v => `$ ${v}`}
                            label={({ value }) => formatMoney(value)}
                            legendOffset={-55}
                        />
                    )}
                </Grid>
            </Grid>
            <Grid item xs={12} container alignContent="flex-start" spacing={2}>
                <Grid item xs={12} lg={6}>
                    <h3 className={styles.title}>Total Guest</h3>
                    {totalGuests.length === 0 ? (
                        <Grid item className={styles.gridItem}>
                            <h5>No data available for this date range</h5>
                        </Grid>
                    ) : (
                        <BarChart
                            data={totalGuests}
                            keys={['Number of Guests']}
                            colors={{ scheme: 'paired' }}
                            axisLeftLegend={'Number of Guests'}
                            legendOffset={-45}
                        />
                    )}
                </Grid>
                <Grid item xs={12} lg={6}>
                    <h3 className={styles.title}>Total Voucher</h3>
                    {totalVoucher.length === 0 ? (
                        <Grid item className={styles.gridItem}>
                            <h5>No data available for this date range</h5>
                        </Grid>
                    ) : (
                        <BarChart
                            data={totalVoucher}
                            keys={['Total Voucher']}
                            colors={{ scheme: 'paired' }}
                            axisLeftLegend={'Total Voucher'}
                            legendOffset={-45}
                        />
                    )}
                </Grid>
            </Grid>
            <Grid item xs={12} container alignContent="flex-start" spacing={2}>
                <Grid item xs={12} lg={12}>
                    <h3 className={styles.title}>Total Commission</h3>
                    {totalCommission.length === 0 ? (
                        <Grid item className={styles.gridItem}>
                            <h5>No data available for this date range</h5>
                        </Grid>
                    ) : (
                        <BarChart
                            data={totalCommission}
                            keys={['Total Commission']}
                            colors={{ scheme: 'category10' }}
                            axisLeftLegend={'Total Commission'}
                            format={v => `$ ${v}`}
                            label={({ value }) => formatMoney(value)}
                            legendOffset={-50}
                        />
                    )}
                </Grid>
            </Grid>
            <Grid item xs={12} container alignContent="flex-start" spacing={2}>
                <Grid item xs={12} lg={6}>
                    <h3 className={styles.title}>Number of Refunds</h3>
                    {refundStats.length === 0 ? (
                        <Grid item className={styles.gridItem}>
                            <h5>No data available for this date range</h5>
                        </Grid>
                    ) : (
                        <BarChart
                            data={refundStats}
                            keys={['Number of Refunds']}
                            colors={{ scheme: 'set3' }}
                            axisLeftLegend={'Number of Refunds'}
                            legendOffset={-45}
                        />
                    )}
                </Grid>
                <Grid item xs={12} lg={6}>
                    <h3 className={styles.title}>Total Refund</h3>
                    {refundStats.length === 0 ? (
                        <Grid item className={styles.gridItem}>
                            <h5>No data available for this date range</h5>
                        </Grid>
                    ) : (
                        <BarChart
                            data={refundStats}
                            keys={['Total Refund']}
                            colors={{ scheme: 'set2' }}
                            axisLeftLegend={'Total Refund'}
                            format={v => `$ ${v}`}
                            label={({ value }) => formatMoney(value)}
                            legendOffset={-50}
                        />
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default StatisticsTable;
